class CustomPincodeChecker extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.input = this.querySelector('.pincode-checker__input');
    this.button = this.querySelector('.pincode-checker__button');
    this.result = this.querySelector('.pincode-checker__result');
    this.htoSection = this.querySelector('.pincode-checker__hto');

    this.sheetId = this.dataset.sheetId;
    this.range = this.dataset.sheetRange;
    this.apiKey = this.dataset.apiKey;
    this.colPincode = parseInt(this.dataset.colPincode) - 1;
    this.colMessage = parseInt(this.dataset.colMessage) - 1;
    this.colDays = parseInt(this.dataset.colDays) - 1;

    // Parse HTO column — use null if setting is blank/missing
    const rawHto = this.dataset.colHto;
    this.colHto = (rawHto && rawHto.trim() !== '' && !isNaN(parseInt(rawHto)))
      ? parseInt(rawHto) - 1
      : null;

    if (this.input) {
      this.input.setAttribute('maxlength', '6');
      this.input.addEventListener('input', () => {
        this.input.value = this.input.value.replace(/\D/g, '').slice(0, 6);
      });
    }

    if (!this.input || !this.button || !this.result) {
      console.warn('PincodeChecker: Missing required elements.');
      return;
    }

    if (!this.sheetId || !this.range || !this.apiKey) {
      this.setResult('Missing Google Sheets configuration.', 'error');
      return;
    }

    this.button.addEventListener('click', () => this.checkPincode());

    // Also allow pressing Enter inside the input
    this.input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') this.checkPincode();
    });

    // Initialize auto-fetch pincode feature
    this.initAutoFetch();
  }

  async initAutoFetch() {
    // 1. Check if user already has a saved pincode in localStorage
    const savedPincode = localStorage.getItem('yourspex_saved_pincode');
    if (savedPincode && /^\d{6}$/.test(savedPincode)) {
      this.input.value = savedPincode;
      
      // Auto-check on first load of the session to show the message
      if (!sessionStorage.getItem('yourspex_pincode_checked_this_session')) {
        this.checkPincode();
      }
      return;
    }

    // 2. Prevent fetching multiple times per session
    if (sessionStorage.getItem('yourspex_pincode_auto_fetched')) return;
    sessionStorage.setItem('yourspex_pincode_auto_fetched', 'true');

    // 3. Try to fetch location using browser Geolocation API
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const lat = position.coords.latitude;
          const lon = position.coords.longitude;
          
          try {
            // Using OpenStreetMap Nominatim for free reverse geocoding
            const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lon}`);
            const data = await res.json();
            
            const postcode = data?.address?.postcode;
            if (postcode && /^\d{6}$/.test(postcode)) {
              this.input.value = postcode;
              this.checkPincode(); // Automatically check it
            } else {
              console.warn('[PincodeChecker] No valid postcode found in geocoding response.', data);
            }
          } catch (err) {
            console.warn('[PincodeChecker] Reverse geocoding failed:', err);
          }
        },
        (error) => {
          console.warn('[PincodeChecker] Geolocation blocked or failed:', error.message);
        },
        { timeout: 10000, maximumAge: 60000 } // Timeout after 10s, allow cached location up to 1min
      );
    } else {
      console.warn('[PincodeChecker] Geolocation API not supported by this browser.');
    }
  }

  async checkPincode() {
    const pincode = this.input.value.trim();

    if (!pincode) {
      this.setResult('Please enter a pincode.', 'error');
      this.hideHtoSection();
      return;
    }

    if (!/^\d{6}$/.test(pincode)) {
      this.setResult('Enter a valid 6-digit pincode.', 'error');
      this.hideHtoSection();
      return;
    }

    // Save valid pincodes so they persist across pages and reloads
    localStorage.setItem('yourspex_saved_pincode', pincode);
    sessionStorage.setItem('yourspex_pincode_checked_this_session', 'true');

    this.setResult('Checking...', 'info');
    this.hideHtoSection();

    try {
      const data = await this.fetchSheetData();
      const match = data.find(row => String(row[this.colPincode]).trim() === pincode);

      if (!match) {
        this.setResult('Pincode not serviceable.', 'error');
        this.hideHtoSection();
        return;
      }

      const message = match[this.colMessage] || '';
      const daysRaw = match[this.colDays] || '';

      // Determine HTO value from the sheet row
      let htoValue = 'no'; // default
      if (this.colHto !== null) {
        const rawVal = match[this.colHto];
        if (rawVal !== undefined && rawVal !== null) {
          htoValue = String(rawVal).trim().toLowerCase();
        }
      }

      console.log('[PincodeChecker] HTO column index:', this.colHto, '| Raw HTO value:', match[this.colHto], '| Normalised:', htoValue);

      // Build delivery date string
      const dayMatches = daysRaw.match(/\d+/g)?.map(Number) || [];
      let deliveryText = '';
      if (dayMatches.length >= 2) {
        const [minDay, maxDay] = dayMatches;
        deliveryText = `<br><strong>Estimated Delivery:</strong> ${this.getEstimatedDate(minDay)} to ${this.getEstimatedDate(maxDay)}`;
      } else if (dayMatches.length === 1) {
        deliveryText = `<br><strong>Estimated Delivery:</strong> ${this.getEstimatedDate(dayMatches[0])}`;
      }

      this.setResult(`${message}${deliveryText}`, 'success');

      // Show or hide the HTO banner
      if (htoValue === 'yes') {
        this.showHtoSection();
      } else {
        this.hideHtoSection();
      }

    } catch (err) {
      console.error('Pincode fetch error:', err);
      this.setResult('Something went wrong. Try again later.', 'error');
      this.hideHtoSection();
    }
  }

  async fetchSheetData() {
    const url = `https://sheets.googleapis.com/v4/spreadsheets/${this.sheetId}/values/${this.range}?key=${this.apiKey}`;
    const response = await fetch(url);
    const json = await response.json();
    return json.values?.slice(1) || [];
  }

  getEstimatedDate(days) {
    const date = new Date();
    date.setDate(date.getDate() + days);
    return date.toLocaleDateString(undefined, {
      weekday: 'long',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  setResult(message, type = 'info') {
    this.result.className = `result ${type}`;
    this.result.innerHTML = message;
  }

  showHtoSection() {
    if (this.htoSection) {
      this.htoSection.style.display = 'block';
      console.log('[PincodeChecker] HTO banner shown.');
    } else {
      console.warn('[PincodeChecker] .pincode-checker__hto element not found in DOM.');
    }
  }

  hideHtoSection() {
    if (this.htoSection) {
      this.htoSection.style.display = 'none';
    }
  }
}

customElements.define('custom-pincode-checker', CustomPincodeChecker);